import localforage from 'localforage';
import { platformService } from '../services/platform.service';

const cacheSchema: LocalForageOptions = {
  driver: localforage.INDEXEDDB,
  name: 'tikit',
  storeName: 'nodes',
  version: 1
};

const storageReadTimeout = 5 * 1000;    // ceiling on a single IndexedDB read before we treat it as a miss
const subscriptionTimeout = 15 * 1000;  // ceiling on the refresh-subscription wait before we answer empty
const cache: LocalForage = localforage.createInstance(cacheSchema);
const platformAPI = new platformService();

// We need an dictionary for those calls that check before we return all the features, this way we can 'subscribe' to the cache getting set
const observer: { [id: string]: Array<(nodes) => void> } = {};

const pushSubscription = (key: string, callback: (enabled) => void) => {
  if (!observer[key]) observer[key] = [];
  observer[key].push(callback);
}

/**
 * refreshCustomNodes is a one-shot: App.loadPage awaits it once and it is never called again.
 * Every getNodesByType() subscription is released by that single run - so once it has settled, a
 * later lookup for a type that is still unknown must NOT subscribe, because nothing will ever wake
 * it. This flag lets that path fall through to a direct cache re-read instead. Mirrors the pattern
 * in FeatureCache.tsx.
 */
let refreshSettled = false;

// Resolve every outstanding subscription. Used when the refresh fails so that no getNodesByType()
// promise - and no component awaiting one, such as SideNav - is left pending.
const flushObservers = (nodes: CustomNode[]) => {
  Object.keys(observer).forEach(key => {
    const waiters = observer[key];
    observer[key] = [];
    waiters?.forEach(resolve => resolve(nodes));
  });
};

export enum NodeType {
  Page = "Page",
  Tab = "Tab"
}

export const clearNodeCache = async () => {
  refreshSettled = false;
  await cache.clear();
};

export const refreshCustomNodes = async (): Promise<boolean> => {
  try {
    const { data: { value: nodes } } = await platformAPI.getCustomNodes();
    // Split the data
    const nodeGrouping: Record<string, CustomNode[]> = nodes.reduce((groups, node: CustomNode) => {
      groups[node.ItemType].push(node);
      return groups;
    }, { [NodeType.Page]: [], [NodeType.Tab]: [] });

    // Notify every waiter before touching storage. This used to happen per key, interleaved with
    // that key's own `await cache.setItem` - so a getNodesByType() call whose own storage read
    // resolved in that gap would subscribe into an observer array this loop had already drained,
    // and nothing would ever wake it (see the identical fix in FeatureCache.tsx).
    for (const [key, value] of Object.entries(nodeGrouping)) {
      observer[key]?.forEach(i => i(value));
      observer[key] = [];
    }
    refreshSettled = true;

    // Persist in parallel now that every waiter already has its answer. Failures are tolerable:
    // the values above are what a still-open getNodesByType() call already received.
    await Promise.all(
      Object.entries(nodeGrouping).map(([key, value]) => cache.setItem<CustomNode[]>(key, value).catch(() => undefined))
    );

    return true;
  } catch (err) {
    console.error(err);

    // The fetch failed, so the values it would have published are never coming on this load. Wake
    // every waiter with an empty list rather than leaving getNodesByType() promises - and the
    // components awaiting them, such as SideNav - pending for the life of the session.
    flushObservers([]);
  } finally {
    // Whether it succeeded or failed, the one-shot has now run. A later lookup for a type that is
    // still unknown must take the direct cache path in awaitRefresh instead of subscribing.
    refreshSettled = true;
  }

  return false;
}

// A stalled IndexedDB transaction never resolves or rejects, so a bare `await cache.getItem` can
// wedge the caller. Cap the wait and treat a timeout as a cache miss.
const readFromStorage = (type: NodeType): Promise<CustomNode[] | null> => {
  let timer: ReturnType<typeof setTimeout>;
  const timedOut = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), storageReadTimeout); });

  return Promise.race([cache.getItem<CustomNode[]>(type).catch(() => null), timedOut])
    .then(value => { clearTimeout(timer); return value ?? null; });
};

/**
 * Wait for refreshCustomNodes to publish this type - but never indefinitely. If the refresh has
 * already settled it will not run again, so re-read the cache directly (refreshCustomNodes writes
 * it before notifying); otherwise subscribe, with a timeout that resolves empty so a caller such as
 * SideNav still renders (without custom nodes) rather than never resolving at all.
 */
const awaitRefresh = (type: NodeType): Promise<CustomNode[]> => {
  if (refreshSettled) return readFromStorage(type).then(result => result ?? []);

  return new Promise<CustomNode[]>(resolve => {
    let done = false;
    const settle = (nodes: CustomNode[]) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(nodes);
    };
    const timer = setTimeout(() => settle([]), subscriptionTimeout);
    pushSubscription(type, settle);
  });
};

export const getNodesByType = async (type: NodeType): Promise<CustomNode[]> => {
  const result = await readFromStorage(type);

  if (result === undefined || result === null)
    return awaitRefresh(type);

  return result;
}
export const getNodeById = async (type: NodeType, id: number): Promise<CustomNode> => {
  const nodes = await getNodesByType(type);
  return nodes?.find(x => x.Id == id);
}